import { useCallback } from "react";
import classNames from "classnames";
import { Grid } from "~/common/mui";
import { Card } from "~/features/configurator/Linear/components/Card";
import { Controller, useFormContext } from "react-hook-form";
import {
  type CheckboxSelectProps,
  isActive,
  type RHFCheckboxSelectProps,
  useOnElementClick,
} from "~/features/selectionAssistant/housing/components/Inputs/CheckboxSelect/CheckboxSelect";
import { useHSAFacetData } from "~/features/selectionAssistant/housing/components/useHSAFacetData";
import {
  type ItemDefinitionV2StateData,
  type TileDefinitionV2,
} from "~/features/selectionAssistant/housing/types/FilterDefinitionV2";
import { CheckedCircle } from "~/common/components/CheckedCircle/CheckedCircle";

export interface TileSelectProps
  extends Omit<CheckboxSelectProps<TileDefinitionV2 & ItemDefinitionV2StateData>, "variant"> {
  variant?: TileSelectVariant;
}

export enum TileSelectVariant {
  ICN_TOP = "img-top",
  ICN_LEFT = "img-left",
}

export const TileSelect = ({
  className,
  label,
  value,
  values,
  multiple,
  onChange,
  "data-test-id": testId,
  variant = TileSelectVariant.ICN_TOP,
}: TileSelectProps) => {
  const onTileClick = useOnElementClick(value, onChange, multiple);

  const gridColSize = variant === TileSelectVariant.ICN_TOP ? 4 : 12;

  let cls = "scha__tile-select";
  cls = `${cls} ${cls}--${variant}`;

  return (
    <div className={classNames(cls, className)} data-test-id={testId}>
      {label && <label className={"scha__tile-select__label"}>{label}</label>}
      <Grid container className={"scha__tile-select__tile-container"}>
        {values.map((tileDef) => (
          <Grid
            key={tileDef.code}
            size={{
              phone: gridColSize,
            }}
          >
            <Tile
              {...tileDef}
              variant={variant}
              selected={isActive(value, tileDef.code)}
              onClick={onTileClick}
              data-test-id={`${testId}-${tileDef.code}`}
              disabled={tileDef.disabled && !isActive(value, tileDef.code)}
            />
          </Grid>
        ))}
      </Grid>
    </div>
  );
};

interface TileProps extends TileDefinitionV2, ItemDefinitionV2StateData {
  onClick: (code: string) => void;
  "data-test-id"?: string;
  variant: TileSelectVariant;
  selected?: boolean;
}

const Tile = ({
  selected,
  disabled,
  activeImageUrl,
  imageUrl,
  label,
  code,
  onClick,
  variant,
  "data-test-id": testId,
}: TileProps) => {
  const onClickHandler = useCallback(() => onClick(code), [onClick, code]);

  const img = selected && activeImageUrl ? activeImageUrl : imageUrl;

  let cls = "scha__tile-select__tile";
  cls = `${cls} ${cls}--${variant}`;

  return (
    <Card className={cls} disabled={disabled} onClick={onClickHandler} active={selected} data-test-id={testId}>
      {img && (
        <div className={"scha__tile-select__tile__image-container"}>
          <img src={img} className={"scha__tile-select__tile__image"} alt={""} />
        </div>
      )}
      <div className={"scha__tile-select__tile__content"}>
        <span className={"scha__tile-select__tile__label"}>{label}</span>
      </div>
      <CheckedCircle className="scha__tile-select__check" checked={selected} />
    </Card>
  );
};

export interface RHFTileSelectProps extends Omit<RHFCheckboxSelectProps, "columnAmount"> {
  variant?: TileSelectVariant;
  renderGA?: (value: unknown) => void;
}

export const RHFTileSelect = ({ name, renderGA, ...rest }: RHFTileSelectProps) => {
  const { control } = useFormContext();

  return (
    <Controller
      render={({ field: { value, onChange } }) => (
        <TileSelect
          value={value}
          onChange={(e) => {
            onChange(e);
            name === "housing_type" && renderGA && renderGA(e);
          }}
          {...rest}
        />
      )}
      control={control}
      name={name}
    />
  );
};

export interface HousingSATileSelectProps extends Omit<RHFTileSelectProps, "values" | "label"> {}

export const HousingSATileSelect = (props: HousingSATileSelectProps) => {
  const { values, label } = useHSAFacetData(props);

  return <RHFTileSelect values={values} label={label} {...props} />;
};
